import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment as env } from '@environments/environment';
import { EventAttendeeTicketOrder } from '@models/events/event-attendee-ticket-order.model';
import { EventAttendeeTicket } from '@models/events/event-attendee-ticket.model';
import { Event } from '@models/events/event.model';
import { TicketAction } from '@models/tickets/ticket-action.enum';
import { User } from '@models/users/user.model';
import { TranslateModule } from '@ngx-translate/core';
import { TicketOrderCardComponent } from './components/ticket-order-card/ticket-order-card.component';
import { TicketOrderTicketItemComponent } from './components/ticket-order-ticket-item/ticket-order-ticket-item.component';
import { TicketOrderTicketComponent } from './components/ticket-order-ticket/ticket-order-ticket.component';
import { CommonModule } from '@angular/common';
import moment from 'moment';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ForwardTicketBottomSheetComponent } from './dialogs/forward-ticket-bottom-sheet/forward-ticket-bottom-sheet.component';

type TicketCategory = 'myTickets' | 'assignedTickets' | 'unassignedTickets';
@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TicketOrderCardComponent,
    TicketOrderTicketItemComponent,
    TicketOrderTicketComponent,
  ],
  templateUrl: './get-bought-tickets.dialog.html',
  styleUrl: './get-bought-tickets.dialog.scss',
})
export class GetBoughtTicketsDialog {
  loggedUser?: User;
  event?: Event;

  orders?: EventAttendeeTicketOrder[];
  order?: EventAttendeeTicketOrder;
  selectedTicket?: EventAttendeeTicket;

  groupedOrders: Record<string, EventAttendeeTicketOrder[]> = {};
  orderMonths: string[] = [];

  categorizedOrderTickets?: Record<TicketCategory, EventAttendeeTicket[]>;

  constructor(
    public dialogRef: MatDialogRef<GetBoughtTicketsDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private bottomSheet: MatBottomSheet,
  ) {
    if (data) {
      this.loggedUser = data.loggedUser;

      this.event = data.event;
      this.orders = data.orders;

      if (data.ticket) {
        this.selectedTicket = data.ticket;
      } else if (data.order) {
        this.selectOrder(data.order);
      } else if (this.orders) {
        if (this.orders.length === 1) {
          this.selectOrder(data.orders[0]);

          if (this.order?.eventAttendeeTickets.length === 1) {
            this.selectedTicket = this.order.eventAttendeeTickets[0];
          }
        } else if (this.orders.length > 1) {
          this.groupOrdersByMonth(this.orders);
        }
      }
    }
  }

  selectTicket(ticket: EventAttendeeTicket) {
    this.selectedTicket = ticket;
  }

  selectOrder(order: EventAttendeeTicketOrder) {
    this.order = order;
    this.categorizedOrderTickets = this.categorizeTicketsWithRecord(
      this.order.eventAttendeeTickets,
      this.loggedUser,
    );
  }

  forwardTicket(ticket: EventAttendeeTicket) {
    this.bottomSheet.open(ForwardTicketBottomSheetComponent, {
      panelClass: 'new-bottom-sheet-dialog',
      data: {
        ticket: ticket,
        event: this.event,
      },
    });
  }

  goBack() {
    if (this.selectedTicket) {
      this.selectedTicket = undefined;
    } else if (this.order) {
      this.order = undefined;
      this.categorizedOrderTickets = undefined;
    }
  }

  close(isRefresh?: boolean) {
    this.dialogRef.close({ refresh: isRefresh });
  }

  get selectedOrder(): EventAttendeeTicketOrder | undefined {
    if (!this.selectedTicket || !this.orders) {
      return undefined;
    }

    const order = this.orders.find(
      (order) => order.id === this.selectedTicket?.eventAttendeeTicketOrderId,
    );

    return order;
  }

  get showBack(): boolean {
    if (this.selectedTicket) {
      return !!this.order;
    } else if (this.order) {
      return !!this.orders && this.orders.length > 1;
    }

    return false;
  }

  private groupOrdersByMonth(orders: EventAttendeeTicketOrder[]): void {
    const groups: Record<string, EventAttendeeTicketOrder[]> = {};

    for (const order of orders) {
      let date = this.event?.getStartDateTzMoment();
      if (order.eventSlot) {
        date = order.eventSlot.getStartDateTzMoment();
      }
      const key = date ? date.format('MMMM YYYY') : 'Unknown Date';

      if (!groups[key]) {
        groups[key] = [];
      }

      groups[key].push(order);
    }

    this.groupedOrders = groups;

    this.orderMonths = Object.keys(groups).sort((a, b) =>
      moment(b, 'MMMM YYYY').diff(moment(a, 'MMMM YYYY')),
    );
  }

  private categorizeTicketsWithRecord(
    tickets: EventAttendeeTicket[],
    loggedUser?: User,
  ): Record<TicketCategory, EventAttendeeTicket[]> {
    return tickets.reduce<Record<TicketCategory, EventAttendeeTicket[]>>(
      (acc, ticket) => {
        const attendeeId = ticket.eventAttendeeId;
        const userId = ticket.eventAttendee?.userId;

        const category: TicketCategory = attendeeId
          ? loggedUser && userId === loggedUser.id
            ? 'myTickets'
            : 'assignedTickets'
          : 'unassignedTickets';

        acc[category].push(ticket);
        return acc;
      },
      {
        myTickets: [],
        assignedTickets: [],
        unassignedTickets: [],
      },
    );
  }

  protected env = env;
  protected readonly TicketAction = TicketAction;
}
